import React, { useState } from 'react';
import { Button, Typography, Grid, Paper, Box, Checkbox, FormControlLabel, useMediaQuery } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { TextInput, DateInput, Dropdown } from 'qubCommon/Inputs';
import { useTranslation } from 'react-i18next';
import useSettings from 'hooks/useSettings';
import { useTheme } from '@emotion/react';
import { useNavigate } from 'react-router';
import ReCAPTCHAPolicy from 'ui-component/recaptchaPolicy';
import ReCAPTCHA from 'react-google-recaptcha';
import { useYupValidationResolver } from 'hooks/yupValidation/useYupValidationResolver';
import * as yup from 'yup';
import FormCheckbox from 'qubCommon/Inputs/Checkbox';
import { createOneTimeAccessLink } from 'apis/controllers/membership';
import { useSnackbar } from 'notistack';
import { useQuery } from 'react-query';
import { getLookups } from 'apis/controllers/backoffice';
import { LoadingButton } from '@mui/lab';
import EmailSentSuccess from './EmailSentSuccess';

const recaptchaKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

const RegisterPage = () => {
    const { t } = useTranslation();
    const { projectLogo } = useSettings();
    const theme = useTheme();
    const reCaptchaRef = React.useRef();
    const [submitLoading, setSubmitLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const yupResolver = useYupValidationResolver(
        yup.object().shape({
            firstName: yup.string().max(255).required(t('FirstNameRequired')),
            lastName: yup.string().max(255).required(t('LastNameRequired')),
            email: yup.string().max(255).required(t('EmailRequired')).email(t('EmailNotValid')),
            allowDataCollection: yup.boolean().oneOf([true], 'You must agree to proceed.'),
            organizationId: yup.string().required(t('ProvinceRequired'))
        })
    );
    const methods = useForm({
        defaultValues: {
            email: '',
            firstName: '',
            organizationId: null,
            allowDataCollection: false
        },
        resolver: yupResolver
    });

    const { enqueueSnackbar } = useSnackbar();

    const sm = useMediaQuery(theme.breakpoints.down('sm'));

    const onSubmit = async (data) => {
        setSubmitLoading(true);
        const token = await reCaptchaRef.current.executeAsync();
        const res = await createOneTimeAccessLink({ ...data, token });
        if (res?.status === 'success') {
            setEmailSent(true);
            methods.reset();
        }

        try {
            enqueueSnackbar(t(res?.message), {
                variant: res?.status
            });
        } catch (error) {
            enqueueSnackbar(t('SomethingWrong'), {
                variant: 'error'
            });
        } finally {
            setSubmitLoading(false);
        }
    };

    const navigate = useNavigate();

    const provincesQuery = useQuery('provinces', async () => {
        const res = await getLookups({ dataKey: 'OrgProvinces' });
        return res;
    });

    if (emailSent)
        return (
            <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '70vh', backgroundColor: '#E6EEFF' }}>
                <Paper
                    elevation={3}
                    sx={{
                        width: '100%',
                        maxWidth: '900px',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        borderRadius: 2,
                        padding: 8
                    }}
                >
                    <EmailSentSuccess onResend={() => setEmailSent(false)} />
                </Paper>
            </Grid>
        );
    return (
        <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '70vh', backgroundColor: '#E6EEFF', px: 2 }}>
            <Paper
                elevation={3}
                sx={{ width: '100%', maxWidth: '1100px', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, borderRadius: 2 }}
            >
                {/* Left Section */}
                <Box
                    sx={{
                        flex: 1,
                        p: 4,
                        backgroundColor: 'white',
                        borderTopLeftRadius: { sm: 8 },
                        borderBottomLeftRadius: { sm: 8 },
                        display: 'flex',
                        flexDirection: 'column' // Stack items vertically
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem' }}>
                        <img src={projectLogo} alt="Federation Logo" style={{ width: '100%', maxWidth: '150px', marginBottom: '20px' }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontSize: '18px', textAlign: 'center' }} gutterBottom>
                        {t('Join.TitleText')}
                    </Typography>
                    <br />
                    <Typography variant="standard" sx={{ fontSize: '16px', textAlign: 'justify' }}>
                        {t('Join.FirstText')}
                    </Typography>
                    <Typography variant="standard" sx={{ fontSize: '16px', textAlign: 'justify', mt: 2, flexGrow: 1 }}>
                        {t('Join.SecondText')}
                    </Typography>

                    {/* Pushes button to the bottom */}
                    <Box sx={{ marginTop: 1 }}>
                        <Typography variant="standard">{t('AlreadyAccount')}</Typography>
                        <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            fullWidth
                            size="large"
                            sx={{ mt: 'auto' }} // This moves it to the bottom
                            onClick={() => navigate('/login')}
                        >
                            {t('BackToLogin')}
                        </Button>
                    </Box>
                </Box>

                {/* Right Section - Form */}
                <Box
                    sx={{
                        flex: 1.2,
                        p: 4,
                        backgroundColor: '#CFE2FF',
                        borderTopRightRadius: { sm: 8 },
                        borderBottomRightRadius: { sm: 8 },
                        display: 'flex',
                        flexDirection: 'column' // Make it a vertical layout
                    }}
                >
                    <FormProvider {...methods}>
                        <form
                            onSubmit={methods.handleSubmit(onSubmit)}
                            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                        >
                            <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                                <Grid item xs={12}>
                                    <Typography
                                        variant="h3"
                                        align="center"
                                        gutterBottom
                                        marginBottom={2}
                                        sx={{ color: '#0066CC', fontWeight: 'bold' }}
                                    >
                                        {t('BecomeMember')}!
                                    </Typography>
                                </Grid>

                                {/* Membership Province */}
                                <Grid item xs={12}>
                                    <Dropdown
                                        name="organizationId"
                                        label={t('MembershipProvince')}
                                        fullWidth
                                        options={provincesQuery?.data}
                                    />
                                </Grid>

                                {/* Email */}
                                <Grid item xs={12}>
                                    <TextInput name="email" label={t('Email')} fullWidth />
                                </Grid>

                                {/* First & Last Name */}
                                <Grid item xs={12} md={6}>
                                    <TextInput name="firstName" label={t('FirstName')} fullWidth />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextInput name="lastName" label={t('LastName')} fullWidth />
                                </Grid>

                                {/* Data Consent Checkbox */}
                                <Grid item xs={12}>
                                    <FormCheckbox name="allowDataCollection" label={t('AllowDataCollection')} />
                                </Grid>
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <ReCAPTCHAPolicy />
                                </Grid>
                                <Grid item xs={12}>
                                    <ReCAPTCHA
                                        style={{ visibility: 'hidden' }}
                                        sitekey={recaptchaKey}
                                        size="invisible"
                                        ref={reCaptchaRef}
                                    />
                                </Grid>
                                {/* reCAPTCHA Placeholder */}
                            </Grid>

                            {/* Buttons at the Bottom */}
                            <Box sx={{ mt: 'auto', width: '100%' }}>
                                <LoadingButton
                                    disableElevation
                                    disableRipple
                                    loading={submitLoading}
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                >
                                    {t('Join')}
                                </LoadingButton>
                            </Box>
                        </form>
                    </FormProvider>
                </Box>
            </Paper>
        </Grid>
    );
};

export default RegisterPage;
